import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const gtcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const gtcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const gtcPrograms = [{
  slug: 'diploma-business-management',
  title: 'Diploma in Business Management',
  shortTitle: 'Business Management',
  mProgram: '63926'
}, {
  slug: 'diploma-computer-science',
  title: 'Diploma in Computer Science',
  shortTitle: 'Computer Science',
  mProgram: '63928'
}, {
  slug: 'aab-business-management',
  title: 'Associate of Applied Business (A.A.B.) in Business Management',
  shortTitle: 'A.A.B. Business Management',
  mProgram: '63927'
}, {
  slug: 'aas-computer-science',
  title: 'Associate of Applied Science (A.A.S.) in Computer Science',
  shortTitle: 'A.A.S. Computer Science',
  mProgram: '63929'
}];
export function GTCLandingPage() {
  usePageMeta('Global Tech College | Career-Focused Degree Programs', "Explore online degree programs at Global Tech College. Career-focused degree and certificate programs designed to prepare students for success in today's competitive job market.", {
    ogImage: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug && formStep === 0) {
      setFormStep(1);
    }
  };
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = gtcPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7mchg3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Global Tech College',
            logo: 'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
            primaryColor: '#1B365D',
            accentColor: '#8B1A1A',
            backUrl: '/colleges/global-tech-college',
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`;
  const currentProgramTitle = gtcPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-1143" data-mp="11jmmj2b9rbwd">
          <div data-id="element-1144" data-mp="11gi87c43azrz">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1145" data-mp="16ho2hedeev3m">
              Select a Program
            </label>
            <div className="relative" data-id="element-1146" data-mp="sjzis9v2hu05">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#1B365D] focus:border-[#1B365D] font-medium text-gray-800" data-id="element-1147" data-mp="wiytyc7e6n01">
                <option value="" data-id="element-1148" data-mp="crnzh2uvbpu8">-- Choose a Program --</option>
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1149" data-mp="7stblmi1nb39">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-1150" data-mp="3s9daflqi1fj">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1151" data-mp="3uyu8mbitnvid" />
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-1152" data-mp="vhw27junoe9w">
            Select a program above to get started
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-1153" data-mp="mp266aqzp09c">
          <div data-id="element-1154" data-mp="mo95nrimj6zn">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1155" data-mp="mnr4cgh3u2kv">
              Program
            </label>
            <div className="relative" data-id="element-1156" data-mp="1f7xz33n94uer">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-1157" data-mp="1afogy9sdoyn1">
                {gtcPrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-1158" data-mp="1mq5yywue4eo7">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-1159" data-mp="1vqm0i0rkpbqf">
                <ChevronDownIcon className="h-4 w-4" data-id="element-1160" data-mp="20q70meihbq4q" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-1161" data-mp="25o04h0wb881u" />

          <div data-id="element-1162" data-mp="25mf8d6uckh1v">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1163" data-mp="2afqibgughdy0">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-1164" data-mp="2jg6jukrn2b08">
              <option value="" data-id="element-1165" data-mp="2ocx29eqx1iot">-- Please Select --</option>
              {gtcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-1166" data-mp="1xpfuwfvukixv">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1167" data-mp="27nvocpick4aj">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-1168" data-mp="2chzx9eb6ame9">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1169" data-mp="2gi0lfqo2qbyi">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-1170" data-mp="2lioboa4haoro">
              <option value="" data-id="element-1171" data-mp="2veizrkrh1c1u">-- Please Select --</option>
              {gtcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-1172" data-mp="w38bhk8j8ddx">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-1173" data-mp="3eb1bs07zlna4">{errors.startDate}</p>}
          </div>

          <div data-id="element-1174" data-mp="39kkycg4621nr">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1175" data-mp="39k1cwpfb4v4z">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-1176" data-mp="30jv77ppuozgd">
              <option value="" data-id="element-1177" data-mp="13ygjftnasvy">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-1178" data-mp="3t9228eq3j6wv">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-1179" data-mp="3obad1sgu5ywd">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1180" data-mp="3jcgvnoyfhnqx">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1181" data-mp="2cukbimncxv93">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1182" data-mp="29d61vth4w1vp" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1183" data-mp="2eaxvqt1c9y2j">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-1184" data-mp="1vd51sg8p0yha">
          <div data-id="element-1185" data-mp="1ve6cukpplkz0">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1186" data-mp="1vdwp73s4zpwh">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-1187" data-mp="1lntv109a45wk" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1188" data-mp="o20n9rvb82zk">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-1189" data-mp="12z1apj6gxbs8">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-1190" data-mp="xzexvaujxxjy" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1191" data-mp="1bx3j4jsj76pj">
                <div data-id="element-1192" data-mp="16z1znxr715dj">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1193" data-mp="16zk076kw25r9">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="City" data-id="element-1194" data-mp="1gqxvt39pylf4" />
                </div>
                <div data-id="element-1195" data-mp="3k606fskwtwvy">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1196" data-mp="3k6jemhkrd6ws">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#1B365D] focus:border-[#1B365D] bg-gray-50" placeholder="State" data-id="element-1197" data-mp="3txxjl4vhayxb" />
                </div>
              </div>
              <div data-id="element-1198" data-mp="3694kizjk8ayh">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1199" data-mp="3b64rrds5bjr6">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#1B365D] focus:border-[#1B365D]'}`} placeholder="123 Main St" data-id="element-1200" data-mp="3f5lyeajb6mxx" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-1201" data-mp="266yfnbnhwynr">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-1202" data-mp="218n5t0x7ao5j">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1203" data-mp="217v89kk1mgcm">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1204" data-mp="1n82l1z7q6i9e" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1B365D] to-[#2A528A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-1205" data-mp="1i9994b836zbc">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-1206" data-mp="3vqcanwtmf4lt">
          <div className="grid grid-cols-2 gap-3" data-id="element-1207" data-mp="4d7tpu1x8tnq">
            <div data-id="element-1208" data-mp="4e1372s9lx9k">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1209" data-mp="4eiz1yb0m9gy">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-1210" data-mp="3rrd7agnjffwh" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1211" data-mp="nbk75rzv8pp4">{errors.firstName}</p>}
            </div>
            <div data-id="element-1212" data-mp="9bsubul35fcp">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1213" data-mp="9ef13s05jqtv">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-1214" data-mp="2u1s1w2y7xmes" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1215" data-mp="2rgaigy6sqzra">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1216" data-mp="2ml4yw60v9x6d">
            <div data-id="element-1217" data-mp="30j1xf7yvsdno">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1218" data-mp="30hihxjl1u6my">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-1219" data-mp="27tmm0h3b0ir8" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1220" data-mp="2hkzcp5dd92vo">{errors.email}</p>}
            </div>
            <div data-id="element-1221" data-mp="2csz5cruyv7p2">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-1222" data-mp="2csfpdgovmtdo">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-1223" data-mp="1k53fa5zwke19" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1224" data-mp="13ktvca7c3mup">{errors.phone}</p>}
            </div>
          </div>

          <div className="space-y-2 pt-2" data-id="element-1225" data-mp="18i3hngzffieb">
            <label className="flex items-start gap-2.5 cursor-pointer" data-id="element-1226" data-mp="1di83lmj3t5bx">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1B365D] focus:ring-[#1B365D] cursor-pointer flex-shrink-0" data-id="element-1227" data-mp="1dg51430rljqb" />
              <span className="text-[10px] text-gray-500 leading-tight" data-id="element-1228" data-mp="1raxmvj78zw5c">
                By clicking the Request Info button, you agree to be contacted
                by phone, text message, or email via automated systems by Global
                Tech College about your education at the phone numbers you
                provided above, including any wireless number(s). You are not
                obligated to agree to automated contact to enroll. Note that
                even non-automated calls are recorded for quality assurance.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-1229" data-mp="3gs2y74clon3t">{errors.tcpaConsent}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-1230" data-mp="3bsqfmvsri271">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-1231" data-mp="3bu1hgq2nczvi">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-1232" data-mp="3k87d94nvuzd" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#8B1A1A] to-[#A52A2A] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1233" data-mp="3ur2kw6gjyrxc">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1234" data-mp="2hsk3o1fy7zdx" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-1235" data-mp="23v580ez3qvdi">
      {/* A. Custom Header */}
      <header className="bg-white py-4 px-6 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-1236" data-mp="30lehgs1u5lgk">
        <img src="https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp" alt="Global Tech College Logo" className="h-16 object-contain" data-id="element-1237" data-mp="2wjsxkmqvw6wv" />
      </header>

      {/* B. Hero Section */}
      <section className="bg-gradient-to-br from-[#1B365D] to-[#8B1A1A] relative overflow-hidden" data-id="element-1238" data-mp="2mu9djdrfq70f">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-1239" data-mp="3k955ljah68xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 flex flex-col lg:flex-row items-center gap-10 lg:gap-16 relative z-10" data-id="element-1240" data-mp="3rso3swsai8qr">
          {/* Left Content */}
          <div className="flex-1 text-white max-w-xl" data-id="element-1241" data-mp="4o8b2p3molmp">
            <p className="text-[#D4A843] font-medium mb-3 text-lg tracking-wide" data-id="element-1242" data-mp="4ozuz2nv4ux5">
              Global Tech College Online<sup className="text-xs" data-id="element-1243" data-mp="9mrwmoeb7vej">1</sup>
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6 text-white tracking-tight" data-id="element-1244" data-mp="eluvfr3p55e2">
              {gtcPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Career-Focused
                  <br data-id="element-1245" data-mp="obeasms7ah46" />
                  Degree Programs
                </>}
            </h1>
            <p className="text-lg text-white/85 max-w-lg leading-relaxed" data-id="element-1246" data-mp="sfdqx49kjdx0">
              Global Tech College offers career-focused degree and certificate
              programs designed to prepare students for success in today's
              competitive job market. With flexible online options and dedicated
              support, we help you achieve your educational goals.
            </p>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-8 sm:p-10 flex-shrink-0 relative z-10" data-id="element-1247" data-mp="2yf3o57giv9bx">
            <form onSubmit={e => e.preventDefault()} id="gtc-lead-form" data-id="element-1248" data-mp="3d2gauzi2fl3w">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-1249" data-mp="3879c863nl6lu" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-1250" data-mp="3m2brvkyayabu" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B365D] mb-4" data-id="element-1251" data-mp="3h6edhk6lne1p">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-1252" data-mp="1mt4y5brdgybj">
                <div className="bg-gradient-to-r from-[#1B365D] to-[#2A528A] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-1253" data-mp="2auhpqx9hmdj"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-1254" data-mp="3pkiw6gq7n2r0">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1255" data-mp="3tnf9brwze6nk">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-gray-50 py-12 border-b border-gray-200" data-id="element-1256" data-mp="14wwct39fi2af">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1257" data-mp="18wycj6vlsln2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1258" data-mp="k7bya7ed99m5">
            <div className="flex flex-col items-center text-center" data-id="element-1259" data-mp="k6k8jd7ga63s">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1260" data-mp="f9ae1j57f493">
                <LaptopIcon className="w-8 h-8" data-id="element-1261" data-mp="a7b944ttfza1" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1262" data-mp="591d5s09ound">
                100% Online Flexibility
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1263" data-mp="19si8jthqqq8">
                Study on your schedule with 24/7 access to coursework.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1264" data-mp="3nn97oe4pedjf">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1265" data-mp="3iozc42f2haxa">
                <BriefcaseIcon className="w-8 h-8" data-id="element-1266" data-mp="3dqed26bjwxaf" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1267" data-mp="39o9j16h6xyn9">
                Career-Focused Curriculum
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1268" data-mp="1p70cvnw9v55x">
                Gain practical skills designed for today's job market.
              </p>
            </div>
            <div className="flex flex-col items-center text-center" data-id="element-1269" data-mp="1t9omvywq2i4h">
              <div className="w-16 h-16 bg-[#1B365D]/10 rounded-full flex items-center justify-center mb-4 text-[#1B365D]" data-id="element-1270" data-mp="1y7ge0qpjm07m">
                <AwardIcon className="w-8 h-8" data-id="element-1271" data-mp="233x9y72zhiv9" />
              </div>
              <h3 className="text-lg font-bold text-[#1B365D] mb-2" data-id="element-1272" data-mp="15jkgjjs5g49s">
                Accredited Institution
              </h3>
              <p className="text-sm text-gray-600" data-id="element-1273" data-mp="1af9ni86i0ng4">
                Accredited by ACCET, ensuring quality education standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* D. Content Section */}
      <section className="py-16 bg-white" data-id="element-1274" data-mp="ms9x1nor6trx">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1275" data-mp="mrhyc3zv0fxf">
          <h2 className="text-3xl font-bold text-[#1B365D] mb-6" data-id="element-1276" data-mp="rptawmhjh4jc">
            Why Choose Global Tech College?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1277" data-mp="joc780gwe5dj">
            At Global Tech College, we are committed to providing accessible,
            high-quality education that empowers students to achieve their
            professional goals. Our programs are designed with input from
            industry experts to ensure you learn the skills employers are
            looking for right now.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 text-left border border-gray-100" data-id="element-1278" data-mp="t45rm2nu9dd">
            <h3 className="text-xl font-bold text-[#1B365D] mb-4" data-id="element-1279" data-mp="3sx6gshm0c2x3">
              Student Support Services
            </h3>
            <ul className="space-y-3" data-id="element-1280" data-mp="5qdvz3xvip29">
              <li className="flex items-start" data-id="element-1281" data-mp="5pm86faipvmj">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1282" data-mp="1m4nh7p6tjxov" />
                <span className="text-gray-700" data-id="element-1283" data-mp="1m6gmp15p4bva">
                  Live Tutor support via email & chat
                </span>
              </li>
              <li className="flex items-start" data-id="element-1284" data-mp="2z24h29bfcr3j">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1285" data-mp="2z5ildn3nfadm" />
                <span className="text-gray-700" data-id="element-1286" data-mp="33zw8l285qb77">
                  Dedicated academic advisors to guide your journey
                </span>
              </li>
              <li className="flex items-start" data-id="element-1287" data-mp="2v2dj9lnhdapi">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1288" data-mp="3ir557vah8wtu" />
                <span className="text-gray-700" data-id="element-1289" data-mp="3itj1g7gw8f2z">
                  Career services assistance including resume building and
                  interview prep
                </span>
              </li>
              <li className="flex items-start" data-id="element-1290" data-mp="38ziv629vl3so">
                <CheckCircle2Icon className="w-5 h-5 text-[#8B1A1A] mr-3 flex-shrink-0 mt-0.5" data-id="element-1291" data-mp="3dzes5hk5nuyq" />
                <span className="text-gray-700" data-id="element-1292" data-mp="5jlkls7sxttt">
                  Flexible learning with pre-recorded and live lectures
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* E. Footer */}
      <footer className="bg-[#1B365D] text-white/70 py-12 text-xs" data-id="element-1293" data-mp="26gb1ucmcpxry">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4" data-id="element-1294" data-mp="21m6tpq1cnjpa">
          <p data-id="element-1295" data-mp="1wpgef4xdcs4x">
            Global Tech College is accredited by The Accrediting Council for
            Continuing Education & Training (ACCET).
          </p>
          <p data-id="element-1296" data-mp="2pd3uc6np2ul8">
            <sup className="text-[10px]" data-id="element-1297" data-mp="2pdvqbil7euc1">1</sup> Online programs are subject to
            state availability. Not all programs are available in all states.
          </p>
          <p className="pt-4 border-t border-white/10" data-id="element-1298" data-mp="2kcg77w2l6xwa">
            © {new Date().getFullYear()} Global Tech College. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>;
}